import { apiService, baseService } from '@clarix/api-client';
import { API_ENDPOINTS } from '@clarix/utils';
import type {
  ProjectListItem,
  CreateProjectPayload,
  UpdateProjectPayload,
  ProjectListParams,
  ProjectListResponse,
  BulkProjectIdsPayload,
  BulkProjectStatusPayload,
  BulkProjectArchivePayload,
  BulkProjectResult,
} from '@clarix/shared-types';

/**
 * Client-side safety net for the archive filter.
 *
 * The server is the real filter (`/projects` excludes archived rows unless
 * asked). This mirrors that contract locally so a server that does not yet
 * honour `is_archived` cannot list live projects under "Archived Projects" —
 * or leak archived ones back into the working lists and the shell sidebar.
 *
 * Rows without an `is_archived` field are treated as NOT archived, so a
 * backend that has not shipped the flag leaves every existing list untouched
 * and simply shows an empty archive.
 *
 * NOTE: `pagination.total` still comes from the server, so it can overcount
 * while the server ignores the param. That resolves itself once the endpoint
 * implements the contract, at which point this filter becomes a no-op.
 *
 * TODO(archive-backend): delete once `/projects` honours `is_archived` /
 * `include_archived`.
 */
function applyArchiveFilter(
  response: ProjectListResponse,
  params?: ProjectListParams,
): ProjectListResponse {
  if (params?.include_archived) return response;
  const wantArchived = params?.is_archived === true;
  return {
    ...response,
    data: response.data.filter((p) => (p.is_archived === true) === wantArchived),
  };
}

export const projectsApi = {
  list: (params?: ProjectListParams) =>
    baseService
      .get<{
        data: ProjectListItem[];
        pagination: { total: number };
      }>(API_ENDPOINTS.PROJECTS, { params })
      .then((res) => applyArchiveFilter(res.data as ProjectListResponse, params)),

  getById: (id: string) => apiService.get<ProjectListItem>(`${API_ENDPOINTS.PROJECTS}/${id}`),

  create: (data: CreateProjectPayload) =>
    apiService.post<ProjectListItem>(API_ENDPOINTS.PROJECTS, data),

  update: (id: string, data: UpdateProjectPayload) =>
    apiService.put<ProjectListItem>(`${API_ENDPOINTS.PROJECTS}/${id}`, data),

  delete: (id: string) => apiService.delete<void>(`${API_ENDPOINTS.PROJECTS}/${id}`),

  bulkDelete: (project_ids: string[]) =>
    apiService.post<BulkProjectResult>(`${API_ENDPOINTS.PROJECTS}/bulk-delete`, {
      project_ids,
    } satisfies BulkProjectIdsPayload),

  bulkSetActive: (project_ids: string[], is_active: boolean) =>
    apiService.patch<BulkProjectResult>(`${API_ENDPOINTS.PROJECTS}/bulk-status`, {
      project_ids,
      is_active,
    } satisfies BulkProjectStatusPayload),

  /**
   * Freeze a project. Without `force` the server refuses with a 409
   * `PROJECT_HAS_RUNNING_TIMERS` (listing the running clocks) rather than
   * archiving out from under someone's timer; re-calling with `force: true`
   * stops those timers — banking the elapsed time — and archives.
   *
   * The check lives inside this call rather than in a separate pre-check
   * endpoint so the happy path is one round trip and no timer can be started
   * in the gap between checking and archiving.
   */
  archive: (id: string, force = false) =>
    apiService.post<ProjectListItem>(`${API_ENDPOINTS.PROJECTS}/${id}/archive`, { force }),

  /** Unfreeze. `status_id` / `is_active` were never touched, so this is lossless. */
  restore: (id: string) =>
    apiService.post<ProjectListItem>(`${API_ENDPOINTS.PROJECTS}/${id}/restore`, {}),

  /** Bulk archive. Same force/409 contract as `archive`. */
  bulkArchive: (project_ids: string[], force = false) =>
    apiService.post<BulkProjectResult>(`${API_ENDPOINTS.PROJECTS}/bulk-archive`, {
      project_ids,
      force,
    } satisfies BulkProjectArchivePayload),

  bulkRestore: (project_ids: string[]) =>
    apiService.post<BulkProjectResult>(`${API_ENDPOINTS.PROJECTS}/bulk-restore`, {
      project_ids,
    } satisfies BulkProjectIdsPayload),
};
